// ============================================================
// موتور پاسخ — سه لایه، به همین ترتیب
//
//   ۱) موتور دستور و مجموعه‌داده‌ها (در مرورگر / API خود سامانه)
//   ۲) پایگاه دانش محلی (بدون شبکه)
//   ۳) مدل زبانی (اختیاری، پیش‌فرض خاموش)
//
// اولین لایه‌ای که جواب دارد برنده است. دلیلش سه چیز است: هزینه
// (بیشتر سوال‌ها جواب ثابت دارند)، سرعت (دو لایه‌ی اول آنی‌اند) و
// تاب‌آوری (بدون مدل هم دستیار کار می‌کند، فقط خشک‌تر).
//
// و یک قاعده‌ی مطلق: دستیار هیچ داده‌ای را نشان نمی‌دهد که نقش
// کاربر در منوی سامانه هم نمی‌بیند.
// ============================================================

import { canAccess, currentUsername, roleLabel, visiblePages, SITE_PAGES } from "./access";
import {
    ChoiceOption,
    Dataset,
    DATASETS,
    QueryResult,
    Slot,
    readSlotValue,
    datasetById,
    unpackRange,
    visibleDatasets,
} from "./datasets";
import { parseRowCount, stripDateExpressions } from "./dates";
import { AssistantFilter, FilterPageId } from "./pageFilter";
import { EXCEL_WORDS, FILTER_WORDS, SHOW_VERBS, Intent, detectIntent } from "./intents";
import { askLlm, canSendResults, isLlmEnabled, isOpenAiMode } from "./llm";
import { findOfflineAnswer } from "./knowledge";
import { narrateResult } from "./narrate";
import { smallTalkReply } from "./smalltalk";
import { hasAny, longestMatch, normalize, stripPatterns } from "./text";

export type MessageSource = "command" | "data" | "knowledge" | "llm" | "fallback" | "system";

export interface QuickAction {
    label: string;
    /** متنی که با کلیک، به جای کاربر فرستاده می‌شود */
    send: string;
}

export interface AssistantMessage {
    id: string;
    role: "user" | "assistant";
    text: string;
    source?: MessageSource;
    result?: QueryResult;
    actions?: QuickAction[];
    /** لینکی که کاربر با کلیک به آن می‌رود */
    link?: { label: string; to: string };
    /** رفتن به صفحه‌ی متناظر، با همان فیلتری که در گفتگو اعمال شد */
    openWith?: { label: string; to: string; filter: AssistantFilter };
    /** گرفتن خروجی اکسل همین نتیجه */
    exportWith?: { label: string; datasetId: string; values: Record<string, string> };
    /**
     * این جمله را بعد از نمایش، با مدل روان کن.
     * پنجره‌ی گفتگو خودش این کار را انجام می‌دهد و متن پیام را
     * جایگزین می‌کند — تا جواب معطل مدل نماند.
     */
    rephrase?: { question: string; summary: string };
}

/**
 * شناسه‌ی ویژه‌ی سوال «در کدام صفحه؟».
 * با یک datasetId واقعی اشتباه نمی‌شود چون هیچ مجموعه‌داده‌ای این
 * نام را ندارد.
 */
export const PAGE_CHOICE = "__page__";

/** گفتگوی نیمه‌کاره: منتظر یک ورودی مشخص از کاربر. */
export interface Pending {
    datasetId: string;
    values: Record<string, string>;
    awaiting: string;
    options?: ChoiceOption[];
}

export type Effect =
    /** filter پر باشد یعنی قبل از رفتن، فیلتر را برای صفحه‌ی مقصد بگذار */
    | { type: "navigate"; link: string; title?: string; filter?: AssistantFilter }
    /** کاربر همین حالا روی همان صفحه است: فقط فیلتر را اعمال کن */
    | { type: "apply-filter"; filter: AssistantFilter; title: string }
    | { type: "export"; datasetId: string; values: Record<string, string> }
    | { type: "close" }
    | { type: "clear" }
    | { type: "voice-on" }
    | { type: "voice-off" }
    | { type: "speak-on" }
    | { type: "speak-off" };

export interface Outcome {
    messages: AssistantMessage[];
    pending: Pending | null;
    effect?: Effect;
    /** متنی که با صدا خوانده می‌شود (خلاصه، نه کل جدول) */
    spoken: string;
}

export interface Turn {
    role: "user" | "assistant";
    content: string;
}

export interface RespondInput {
    message: string;
    role: number | null;
    pending: Pending | null;
    history: Turn[];
    /** خلاصه‌ی آخرین نتیجه — فقط اگر ارسال داده به مدل مجاز باشد */
    lastResultSummary?: string;
    /**
     * خودِ آخرین نتیجه — برای «اطلاعاتشو بگو» که باید همان ردیف‌ها
     * را با صدا بخواند، نه اینکه دوباره از سرور بپرسد.
     */
    lastResult?: QueryResult;
    /**
     * صفحه‌ای که کاربر همین حالا رویش است (یا null اگر فیلترپذیر
     * نیست). برای «فیلان کس را فیلتر کن» که نام صفحه ندارد.
     */
    currentPage?: FilterPageId | null;
}

let seq = 0;
const nextId = () => {
    seq += 1;
    return "m" + Date.now() + "-" + seq;
};

const say = (
    text: string,
    source: MessageSource,
    extra?: Partial<AssistantMessage>
): AssistantMessage => ({
    id: nextId(),
    role: "assistant",
    text,
    source,
    ...(extra || {}),
});

/**
 * سوالی که کاربر «واقعاً» پرسیده.
 *
 * وقتی دستیار اسلاتی را پرسیده و کاربر فقط «رضا محمدی» جواب داده،
 * فرستادن همان دو کلمه به مدل، جمله‌ی بی‌ربط می‌سازد. پیام قبلی کاربر
 * سوال اصلی است.
 */
const originalQuestion = (input: RespondInput): string => {
    const userTurns = input.history.filter((turn) => turn.role === "user");
    const previous = userTurns.length ? userTurns[userTurns.length - 1].content : "";
    return previous || input.message;
};

const statusLabel = (status?: "not_done" | "done"): string =>
    status === "done" ? "انجام‌شده" : status === "not_done" ? "انجام‌نشده" : "";

/** اسلات موقتی، فقط برای خواندن بازه‌ی تاریخ از یک جمله‌ی آزاد. */
const RANGE_PROBE: Slot = { key: "range", label: "", kind: "daterange", prompt: "" };

/** توصیف خوانا از فیلتر، برای پیام و پیام تأیید. */
const describeFilter = (filter: AssistantFilter): string => {
    const parts: string[] = [];
    if (filter.search) parts.push(filter.search);
    if (filter.status) parts.push(statusLabel(filter.status));
    if (filter.startDate) {
        parts.push(
            filter.startDate === filter.endDate
                ? filter.startDate
                : filter.startDate + " تا " + filter.endDate
        );
    }
    if (filter.pageSize) parts.push(filter.pageSize + " سطر");
    return parts.join(" · ");
};

// ---------- منو بر اساس سطح دسترسی ----------

const menuText = (role: number | null): string => {
    const pages = visiblePages(role);
    const data = visibleDatasets(role);

    if (!pages.length) {
        return "نقش شما در این سامانه هیچ صفحه‌ای ندارد. با ادمین سامانه تماس بگیرید.";
    }

    const lines: string[] = [];
    lines.push("نقش شما: " + roleLabel(role) + ".");
    lines.push("");
    lines.push("صفحه‌هایی که می‌توانید ببینید:");
    pages.forEach((p) => lines.push("• " + p.title));

    if (data.length) {
        lines.push("");
        lines.push("داده‌هایی که همین‌جا برایتان می‌آورم:");
        data.forEach((d) => lines.push("• " + d.title));
    }

    return lines.join("\n");
};

const menuActions = (role: number | null): QuickAction[] =>
    visibleDatasets(role)
        .slice(0, 6)
        .map((d) => ({ label: d.title, send: d.title }));

const helpText = (role: number | null): string =>
    [
        "پنج کار می‌توانم بکنم:",
        "",
        "۱) داده نشان بدهم. مثال: «اطلاعات خبازی رضا محمدی» یا «گزارش دیروز».",
        "   اگر چیزی کم باشد، خودم می‌پرسم.",
        "۲) صفحه را فیلتر کنم. مثال: «مهناز کافر فیلتر کن» (روی همین صفحه)",
        "   یا «رضا محمدی را در وظایف راننده، ثبت‌نشده‌ها فیلتر کن».",
        "۳) خروجی اکسل بگیرم. مثال: «خروجی اکسل صفحه اطلاعات خبازی».",
        "۴) صفحه عوض کنم. مثال: «منو ببر به گزارش تخلفات».",
        "۵) درباره‌ی خود سامانه جواب بدهم. مثال: «کم‌پزی یعنی چی؟».",
        "",
        "می‌توانید با میکروفون هم حرف بزنید؛ یک جمله بگویید و منتظر بمانید.",
        "برای شنیدن جواب‌ها، دکمه‌ی بلندگو را روشن کنید.",
        "",
        "هر لحظه بگویید «لغو» تا سوال نیمه‌کاره رها شود.",
        "نقش فعلی شما: " + roleLabel(role) + ".",
    ].join("\n");

// ---------- انتخاب مجموعه‌داده ----------

interface DatasetHit {
    dataset: Dataset;
    score: number;
}

/**
 * امتیاز بر اساس *طول* تگ‌های منطبق، نه تعداد.
 * «سهمیه خبازی» هم با تگ «خبازی» می‌خورد هم با «سهمیه خبازی»؛ با
 * شمارش ساده هر دو امتیاز می‌گرفتند و ترتیب آرایه برنده را تعیین
 * می‌کرد. تگ بلندتر یعنی تطبیق دقیق‌تر.
 */
const matchDataset = (message: string): DatasetHit | null => {
    const n = stripReportGaps(message);
    let best: DatasetHit | null = null;

    for (let i = 0; i < DATASETS.length; i++) {
        const dataset = DATASETS[i];
        let score = 0;
        let allGroupsHit = true;

        for (let g = 0; g < dataset.match.length; g++) {
            const hit = longestMatch(n, dataset.match[g]);
            if (!hit) {
                allGroupsHit = false;
                break;
            }
            score += hit.length;
        }

        if (allGroupsHit && (!best || score > best.score)) {
            best = { dataset, score };
        }
    }

    return best;
};

/** مقدارهایی که از همان جمله‌ی اول قابل برداشت‌اند. */
const extractSlots = (dataset: Dataset, message: string): Record<string, string> => {
    const values: Record<string, string> = {};

    dataset.slots.forEach((slot) => {
        if (slot.kind === "text") {
            // باقی‌مانده‌ی جمله بعد از حذف تاریخ، تعداد سطر، نام صفحه،
            // واژه‌های وضعیت و فعل‌ها = عبارت جست‌وجو
            const extracted = extractFilter(message, dataset);
            if (extracted.search) values[slot.key] = extracted.search;
            return;
        }
        const parsed = readSlotValue(slot, message);
        if (parsed !== null) values[slot.key] = parsed;
    });

    // تعداد سطر: هم روی درخواست API اثر می‌گذارد هم به صفحه تحویل می‌شود
    const rows = parseRowCount(message);
    if (rows) values.size = String(rows);

    // جمله‌ی خام هم همراه می‌رود. مجموعه‌داده‌هایی که پاسخ گفتگویی
    // می‌دهند (سهمیه) باید بتوانند چیزهایی مثل نام ماه را از خود
    // جمله بردارند — چیزی که در اسلات‌ها جا نمی‌شود.
    values.sentence = message;

    // دو پارامتر مخصوص گزارش توزیع آرد
    if (dataset.id === "reports" || dataset.id === "reports-decade") {
        const gaps = extractReportGaps(message);
        if (gaps.voucherDays) values.voucher_days = gaps.voucherDays;
        if (gaps.inspectionDays) values.inspection_days = gaps.inspectionDays;
    }

    return values;
};

// ---------- بیرون کشیدن فیلتر از جمله ----------

/**
 * واژه‌هایی که در هر جمله‌ای می‌آیند و نباید داخل عبارت جست‌وجو
 * بروند. «اسم فلان راننده رو بهم بده» باید به «فلان راننده» برسد،
 * نه به «اسم فلان راننده رو بهم».
 */
const GENERIC_STRIP = [
    "اسم", "نام", "نام خانوادگی", "فامیلی", "در", "صفحه", "برای", "از", "با",
    "رو", "را", "بده", "بدم", "بهم", "نشونم", "نشون", "نمایش", "بیار", "ببین",
    "ببینم", "لیست", "اطلاعات", "رکورد", "رکوردها", "دیتا", "داده", "لطفا",
    "میخوام", "می خوام", "مربوط به", "مال", "های", "ها", "کن", "بزن",
    // فعل‌های گرفتن/ساختن خروجی — وگرنه «بگیر» داخل عبارت جست‌وجو می‌ماند
    "بگیر", "بگیرش", "بگیرید", "بساز", "درست کن", "دانلود", "دانلودش",
    "خروجی", "فایل", "بفرست", "صادر", "و",
    // فعل‌های رفتن. اینها یک باگ واقعی بودند: «برو به اطلاعات خبازی»
    // عبارت «برو به» را داخل کادر جست‌وجوی صفحه می‌گذاشت.
    "برو به", "برو", "ببر", "منو ببر", "بریم", "برم", "باز کن", "بازکن",
    "بازش کن", "منتقل", "منو",
    // واحدهای تعداد سطر
    "سطر", "ردیف", "تا", "مورد", "عدد", "تعداد",
    // واژه‌های خود رابط کاربری. کاربر می‌گوید «تب ثبت‌نشده‌ها» یا
    // «بخش وظایف راننده» — این‌ها اشاره به جای داده‌اند، نه چیزی که
    // باید داخل کادر جست‌وجو برود.
    "تب", "تب ها", "بخش", "قسمت", "جدول", "کادر",
    // «همه» و هم‌خانواده‌هایش یعنی «بدون فیلتر»، پس باید از عبارت
    // جست‌وجو بیرون بروند نه اینکه خودشان جست‌وجو شوند.
    "همه", "همه ی", "همگی", "کل", "تمام", "تمامی",
];

/**
 * باقی‌مانده‌ی جمله هنوز ممکن است زباله باشد.
 *
 * حذف الگوها حرف‌های یتیم جا می‌گذارد («ثبت نشده های» منهای «ثبت
 * نشده ها» یعنی «ی») و اینها اگر به سرور بروند، جست‌وجو همیشه خالی
 * برمی‌گردد. قاعده: اگر کاربر فیلتری نگفته، فیلتری هم اعمال نمی‌شود
 * و کل داده‌ها می‌آید.
 */
const meaningfulSearch = (search: string): string | undefined => {
    const words = search.split(" ").filter((word) => word.length > 1);
    const joined = words.join(" ").trim();
    return joined.length >= 2 ? joined : undefined;
};

/** «۵۰ سطر» یا «تعداد سطر ۵۰» — هم عدد و هم واحدش باید از جست‌وجو برود. */
const ROW_COUNT_PHRASES = [
    /(\d{1,4})\s*(?:سطر|ردیف|تا|رکورد|مورد|عدد)/g,
    /(?:تعداد|سطر|ردیف)\s*(\d{1,4})/g,
];

export interface ExtractedFilter {
    search?: string;
    status?: "not_done" | "done";
    /** تعداد سطر درخواستی */
    pageSize?: number;
}

/**
 * وضعیت، تعداد سطر و عبارت جست‌وجو را از جمله بیرون می‌کشد.
 *
 * ترتیب حذف مهم است: اول تاریخ و تعداد سطر (چون رقم دارند و در
 * جست‌وجو فاجعه می‌سازند)، بعد واژه‌های صفحه و وضعیت، آخر واژه‌های
 * عمومی و فعل‌ها.
 */
export const extractFilter = (sentence: string, dataset?: Dataset): ExtractedFilter => {
    const n = normalize(sentence);

    let status: "not_done" | "done" | undefined;
    // «نشده» قبل از «شده» چک می‌شود، چون «نشده» شامل «شده» است.
    if (/نشده/.test(n)) status = "not_done";
    else if (/شده/.test(n)) status = "done";

    const pageTags: string[] = [];
    SITE_PAGES.forEach((page) => {
        if (!dataset || page.id === dataset.id) pageTags.push.apply(pageTags, page.tags.concat([page.title]));
    });

    const strip = (dataset ? dataset.strip : [])
        .concat(pageTags)
        .concat(FILTER_WORDS)
        .concat(SHOW_VERBS)
        .concat(EXCEL_WORDS)
        .concat(GENERIC_STRIP);

    let base = stripDateExpressions(sentence);
    ROW_COUNT_PHRASES.forEach((pattern) => {
        base = base.replace(pattern, " ");
    });

    const search = stripPatterns(base, strip);
    const pageSize = parseRowCount(sentence);

    return {
        search: meaningfulSearch(search),
        status,
        pageSize: pageSize || undefined,
    };
};

/**
 * عبارت‌های «فاصله زمانی ...» را از جمله حذف می‌کند.
 *
 * این یک باگ واقعی بود: در جمله‌ی «گزارش ۱۴۰۴/۰۳/۱۲ فاصله زمانی
 * تخلیه تا بازرسی ۲۰»، واژه‌ی «بازرسی» باعث می‌شد جمله به
 * «وظایف بازرس» بخورد و گزارش اصلاً گرفته نشود. این‌ها نام
 * پارامترند، نه موضوع جمله.
 */
export const stripReportGaps = (sentence: string): string =>
    normalize(sentence)
        .replace(/فاصله\s*(?:زمانی)?\s*حواله\s*تا\s*تخلیه\s*\d{0,3}/g, " ")
        .replace(/فاصله\s*(?:زمانی)?\s*تخلیه\s*تا\s*بازرسی\s*\d{0,3}/g, " ")
        .replace(/\s+/g, " ")
        .trim();

/**
 * دو پارامتر مخصوص گزارش توزیع آرد:
 * «فاصله زمانی حواله تا تخلیه ۳۰، فاصله زمانی تخلیه تا بازرسی ۳۰»
 */
export const extractReportGaps = (
    sentence: string
): { voucherDays?: string; inspectionDays?: string } => {
    const n = normalize(sentence);
    const voucher = n.match(/حواله\s*تا\s*تخلیه\s*(\d{1,3})/);
    const inspection = n.match(/تخلیه\s*تا\s*بازرسی\s*(\d{1,3})/);
    return {
        voucherDays: voucher ? voucher[1] : undefined,
        inspectionDays: inspection ? inspection[1] : undefined,
    };
};

/**
 * فیلترِ قابل تحویل به صفحه را می‌سازد.
 *
 * صفحه‌ی گزارش‌ها کادر جست‌وجو ندارد ولی کادر تاریخ و دو پارامتر
 * فاصله دارد، پس شکل فیلترش با بقیه فرق می‌کند.
 */
const buildPageFilter = (
    dataset: Dataset,
    values: Record<string, string>,
    sentence: string
): AssistantFilter | undefined => {
    const pageSize = parseRowCount(sentence) || undefined;

    if (dataset.id === "reports") {
        const range = values.range ? unpackRange(values.range) : undefined;
        const gaps = extractReportGaps(sentence);
        if (!range && !pageSize && !gaps.voucherDays && !gaps.inspectionDays) return undefined;

        return {
            pageId: "reports",
            startDate: range ? range.start : undefined,
            endDate: range ? range.end : undefined,
            pageSize,
            voucherToDischargeDays: gaps.voucherDays ? Number(gaps.voucherDays) : undefined,
            dischargeToInspectionDays: gaps.inspectionDays ? Number(gaps.inspectionDays) : undefined,
        };
    }

    if (!dataset.filterable) return undefined;
    if (!values.search && !values.status && !pageSize) return undefined;

    return {
        pageId: dataset.id as FilterPageId,
        search: values.search || undefined,
        status: dataset.hasStatusTab
            ? (values.status as "not_done" | "done" | undefined)
            : undefined,
        pageSize,
    };
};

/** اولین اسلاتی که هم خالی است هم اجباری. */
const missingSlot = (dataset: Dataset, values: Record<string, string>): Slot | null => {
    for (let i = 0; i < dataset.slots.length; i++) {
        const slot = dataset.slots[i];
        const filled = Object.prototype.hasOwnProperty.call(values, slot.key);
        if (filled) continue;
        if (slot.optional) continue;
        if (slot.defaultValue) {
            values[slot.key] = slot.defaultValue;
            continue;
        }
        return slot;
    }
    return null;
};

const askForSlot = async (
    dataset: Dataset,
    values: Record<string, string>,
    slot: Slot
): Promise<Outcome> => {
    let options: ChoiceOption[] | undefined;
    let text = slot.prompt;

    if (slot.kind === "choice" && slot.options) {
        try {
            options = await slot.options(values);
        } catch {
            options = undefined;
        }
        if (!options || !options.length) {
            return {
                messages: [
                    say(
                        "فهرست «" + slot.label + "» از سرور نیامد. یک بار دیگر تلاش کنید یا " +
                            "از صفحه‌ی «" + dataset.title + "» استفاده کنید.",
                        "data",
                        { link: { label: "رفتن به " + dataset.title, to: dataset.link } }
                    ),
                ],
                pending: null,
                spoken: "فهرست " + slot.label + " از سرور نیامد.",
            };
        }
    }

    if (slot.hint) text += "\n(" + slot.hint + ")";

    return {
        messages: [
            say(text, "data", {
                actions: options
                    ? options.slice(0, 12).map((o) => ({ label: o.label, send: o.label }))
                    : undefined,
            }),
        ],
        pending: {
            datasetId: dataset.id,
            values,
            awaiting: slot.key,
            options,
        },
        spoken: slot.prompt,
    };
};

/**
 * جمله‌ی بالای جدول را به مدل می‌سپارد.
 *
 * عمداً *بعد از* نمایش جواب صدا زده می‌شود، نه قبلش.
 *
 * نسخه‌ی اول منتظر مدل می‌ماند و بعد همه چیز را نشان می‌داد؛ یعنی به
 * هر جواب یک تا سه ثانیه اضافه می‌شد و صدا هم همان‌قدر دیر پخش
 * می‌شد — کاربر می‌گفت «دیر حرف می‌زند». حالا جدول و جمله‌ی محلی
 * فوری می‌آیند (و صدا همان لحظه شروع می‌شود) و وقتی مدل جوابش را
 * داد، فقط همان یک خط جایش را می‌گیرد.
 */
export const rephrase = async (
    question: string,
    summary: string,
    role: number | null
): Promise<string | null> => {
    if (!isLlmEnabled() || !canSendResults()) return null;

    return askLlm(
        {
            message:
                "کاربر پرسید: «" + question + "»\n\n" +
                "نتیجه‌ای که سامانه پیدا کرد:\n" + summary,
            history: [],
            context: {
                roleLabel: roleLabel(role),
                allowedPages: [],
                allowedDatasets: [],
            },
        },
        "phrase"
    );
};

/** آیا جمله‌ی جواب باید بعداً با مدل روان شود؟ */
const canRephrase = (): boolean => isLlmEnabled() && canSendResults();

const runDataset = async (
    dataset: Dataset,
    values: Record<string, string>,
    question?: string,
    role?: number | null
): Promise<Outcome> => {
    try {
        const result = await dataset.run(values);

        const actions: QuickAction[] = [];
        // اگر فیلتری نداده و رکوردها بیش از یک صفحه‌اند، پیشنهاد باریک‌تر کردن
        if (!values.search && result.total > result.rows.length) {
            actions.push({ label: "باریک‌تر کردن جست‌وجو", send: dataset.title + " برای " });
        }

        // دکمه‌ی «در خود صفحه با همین فیلتر» — هم برای کاربری که گفته
        // «در صفحه ... نشونم بده» و هم برای وقتی که جدول کوچک پنجره
        // کافی نیست.
        const openWith =
            dataset.filterable && (values.search || values.status)
                ? {
                      label: "نمایش در صفحه با همین فیلتر",
                      to: dataset.link,
                      filter: {
                          pageId: dataset.id as FilterPageId,
                          search: values.search || undefined,
                          status: dataset.hasStatusTab
                              ? (values.status as "not_done" | "done" | undefined)
                              : undefined,
                      },
                  }
                : undefined;

        return {
            messages: [
                say(result.spoken, "data", {
                    result,
                    // جمله را بعداً روان می‌کنیم؛ جواب نباید منتظر مدل بماند
                    rephrase:
                        question && canRephrase()
                            ? { question, summary: summarizeResult(result) }
                            : undefined,
                    link: { label: "باز کردن صفحه‌ی " + dataset.title, to: dataset.link },
                    openWith,
                    exportWith: result.rows.length
                        ? { label: "خروجی اکسل", datasetId: dataset.id, values }
                        : undefined,
                    actions: actions.length ? actions : undefined,
                }),
            ],
            pending: null,
            // صدا همین جمله‌ی محلی را می‌خواند، بدون انتظار برای مدل
            spoken: result.spoken,
        };
    } catch (error: any) {
        const status = error?.response?.status;
        const text =
            status === 403
                ? "این داده هست ولی نقش شما اجازه‌ی دیدنش را ندارد."
                : status === 404
                ? "این مسیر روی سرور پیدا نشد."
                : "گرفتن داده از سرور ناموفق بود. چند لحظه بعد دوباره بپرسید.";
        return {
            messages: [say(text, "data")],
            pending: null,
            spoken: text,
        };
    }
};

/**
 * بعد از نشان دادن رکوردها در گفتگو، همان فیلتر را روی صفحه‌ی
 * متناظر هم می‌گذارد.
 *
 * شرطش فقط این است که فیلتری در کار باشد — نه فعل خاصی. یعنی هر سه
 * شکل گفتن یک کار را می‌کنند:
 *
 *   «گزارش تخلفات فرشته رضایی نمایش بده»
 *   «در گزارش تخلفات فرشته رضایی»
 *   «فرشته رضایی رو در تخلفات فیلتر کن»
 *
 * ولی «گزارش تخلفات» خالی (بدون نام) صفحه را عوض نمی‌کند؛ آنجا کاربر
 * فقط دارد نگاه می‌کند و پرت شدن به صفحه‌ی دیگر آزاردهنده است.
 *
 * فیلتر تاریخ همراه نمی‌رود: کادر فیلتر صفحه‌ها تاریخ ندارد. همین را
 * هم صریح به کاربر می‌گوییم تا فکر نکند صفحه بر اساس تاریخ فیلتر شده.
 */
const withPageHandoff = (
    outcome: Outcome,
    dataset: Dataset,
    values: Record<string, string>,
    sentence: string,
    currentPage?: FilterPageId | null
): Outcome => {
    if (!outcome.messages.length) return outcome;

    const filter = buildPageFilter(dataset, values, sentence);

    // فیلتری در کار نیست («اطلاعات خبازی ها رو نشون بده»): رکوردها را
    // نشان می‌دهیم و کاربر را هم به همان صفحه می‌بریم. اگر از قبل روی
    // همان صفحه است، کاری نمی‌کنیم — فیلتر دستی‌اش را پاک نمی‌کنیم.
    if (!filter) {
        if (currentPage === dataset.id) return outcome;

        const messages = outcome.messages.slice();
        messages[0] = {
            ...messages[0],
            text: messages[0].text + "\nصفحه‌اش را هم باز می‌کنم.",
        };
        return {
            ...outcome,
            messages,
            effect: { type: "navigate", link: dataset.link, title: dataset.title },
        };
    }

    const messages = outcome.messages.slice();
    const first = messages[0];
    // صفحه‌ی گزارش‌ها کادر تاریخ دارد، بقیه ندارند
    const extra =
        values.range && dataset.id !== "reports"
            ? "\nفیلتر تاریخ فقط در همین پنجره اعمال شد؛ کادر فیلتر آن صفحه تاریخ ندارد."
            : "";

    messages[0] = {
        ...first,
        text:
            first.text +
            "\nهمین فیلتر را روی صفحه‌ی «" + dataset.title + "» هم گذاشتم." +
            extra,
    };

    return {
        ...outcome,
        messages,
        effect:
            currentPage === dataset.id
                ? { type: "apply-filter", filter, title: dataset.title }
                : { type: "navigate", link: dataset.link, title: dataset.title, filter },
    };
};

// ---------- خلاصه‌ی نتیجه برای لایه‌ی سوم ----------

/**
 * خلاصه‌ای که به مدل فرستاده می‌شود.
 *
 * عمداً برچسب ستون‌ها همراه مقدار می‌آید: مدل باید بداند «۱۳.۳» سهمیه
 * است نه تعداد کیسه، وگرنه جمله‌ی غلط می‌سازد. و عمداً سقف دارد —
 * فرستادن صد ردیف هم هزینه است هم کمکی به یک جمله‌ی سه‌خطی نمی‌کند.
 */
export const summarizeResult = (result: QueryResult): string => {
    const lines: string[] = [
        result.title +
            " — " + result.total + " رکورد در کل" +
            (result.rows.length !== result.total
                ? "، " + result.rows.length + " مورد نشان داده شد"
                : "") +
            ".",
    ];

    if (result.note) lines.push("توضیح: " + result.note);

    /*
      جوابی که سامانه خودش حساب کرده، *معتبرترین* چیزی است که داریم.
      بعضی مجموعه‌داده‌ها (سهمیه) ماه را از جمله برمی‌دارند و اختلاف با
      ماه قبل را حساب می‌کنند — چیزی که از ردیف‌های خام قابل بازسازی
      نیست. اگر این را نفرستیم، مدل آن جواب دقیق را دور می‌ریزد و حرف
      کلی می‌زند. (این یک باگ واقعی بود: «سهمیه مهر ۱.۵ کیسه» گم می‌شد.)
    */
    if (result.spoken) lines.push("پاسخ محاسبه‌شده‌ی سامانه: " + result.spoken);

    const cell = (row: any, key: string) => {
        const column = result.columns.filter((c) => c.key === key)[0];
        if (!column) return "";
        const value = row[key];
        return column.format ? column.format(value, row) : String(value ?? "");
    };

    // کارت جزئیات (نتیجه‌ی تک) فیلدهای هویتی را دارد — ولی ردیف‌ها هم
    // باید بیایند، وگرنه مدل مثلاً سهمیه‌ی ماه‌به‌ماه را نمی‌بیند.
    if (result.detail) {
        result.detail.forEach((item) => lines.push("- " + item.label + ": " + item.value));
    }

    result.rows.slice(0, 8).forEach((row) => {
        const parts = result.columns
            .slice(0, 6)
            .map((column) => column.label + ": " + cell(row, column.key))
            .filter((part) => !/: (—|undefined|null|)$/.test(part));
        if (parts.length) lines.push("- " + parts.join("، "));
    });

    if (result.rows.length > 8) {
        lines.push("(" + (result.rows.length - 8) + " ردیف دیگر هم هست.)");
    }

    return lines.join("\n");
};

// ---------- دستورها ----------

const handleIntent = async (
    intent: Intent,
    role: number | null,
    sentence: string,
    currentPage?: FilterPageId | null
): Promise<Outcome> => {
    switch (intent.type) {
        case "greeting":
            return {
                messages: [say(intent.reply, "command")],
                pending: null,
                spoken: intent.reply,
            };

        case "navigate": {
            if (!intent.link) {
                return { messages: [say(intent.reply, "command")], pending: null, spoken: intent.reply };
            }

            // اگر در همان جمله فیلتری هم آمده باشد («برو به وظایف
            // راننده، ثبت‌نشده‌ها» یا «... ۵۰ سطر»)، همان فیلتر را با
            // خودمان می‌بریم تا کاربر در صفحه‌ی مقصد دوباره تایپ نکند.
            //
            // ولی عبارت جست‌وجو عمداً همراه نمی‌رود. اگر کاربر جست‌وجو
            // می‌خواست، واژه‌ی «فیلتر کن» را می‌گفت و آن‌وقت اصلاً به
            // اینجا نمی‌رسیدیم (دستور filter زودتر گرفته می‌شود). پس
            // رسیدن به اینجا یعنی کاربر فقط اسم صفحه را گفته — و هر چه
            // از جمله ته‌نشین شده، تکه‌ی همان اسم است نه خواسته‌ی او.
            // «برو به سهمیه خبازی‌ها» صفحه را با کادر جست‌وجوی خالی
            // باز می‌کند، نه با «خبازی» داخلش.
            const dataset = DATASETS.filter((d) => d.link === intent.link)[0];
            let filter: AssistantFilter | undefined;

            if (dataset) {
                const extracted = extractFilter(sentence, dataset);
                const values: Record<string, string> = {};
                if (extracted.status) values.status = extracted.status;
                const range = readSlotValue(RANGE_PROBE, sentence);
                if (range) values.range = range;
                filter = buildPageFilter(dataset, values, sentence);
            }

            const reply = filter
                ? intent.reply + " با فیلتر «" + describeFilter(filter) + "»."
                : intent.reply;

            return {
                messages: [
                    say(reply, "command", {
                        link: { label: "باز کردن " + intent.title, to: intent.link },
                    }),
                ],
                pending: null,
                effect: { type: "navigate", link: intent.link, title: intent.title, filter },
                spoken: reply,
            };
        }

        // ---------- خروجی اکسل ----------
        case "export": {
            if (!intent.pageId) {
                const exportable = visibleDatasets(role);
                return {
                    messages: [
                        say(intent.reply, "command", {
                            actions: exportable
                                .slice(0, 8)
                                .map((d) => ({ label: d.title, send: "خروجی اکسل " + d.title })),
                        }),
                    ],
                    pending: null,
                    spoken: intent.reply,
                };
            }

            const dataset = datasetById(intent.pageId);
            if (!dataset) {
                const text = "«" + (intent.title || "این بخش") + "» داده‌ی قابل خروجی ندارد.";
                return { messages: [say(text, "command")], pending: null, spoken: text };
            }
            if (!canAccess(role, dataset.roles)) {
                const text = "دسترسی شما به «" + dataset.title + "» باز نیست.";
                return { messages: [say(text, "command")], pending: null, spoken: text };
            }

            // فیلتر همان جمله روی خروجی هم اعمال می‌شود:
            // «خروجی اکسل وظایف راننده انجام‌شده»
            const extracted = extractFilter(sentence, dataset);
            const values: Record<string, string> = {};
            if (extracted.search) values.search = extracted.search;
            if (extracted.status) values.status = extracted.status;

            const range = readSlotValue(
                { key: "range", label: "", kind: "daterange", prompt: "" },
                sentence
            );
            if (range) values.range = range;

            return {
                messages: [say(intent.reply, "command")],
                pending: null,
                effect: { type: "export", datasetId: dataset.id, values },
                spoken: intent.reply,
            };
        }

        // ---------- فیلتر روی صفحه ----------
        case "filter": {
            if (intent.reply) {
                return { messages: [say(intent.reply, "command")], pending: null, spoken: intent.reply };
            }

            const pageId = (intent.pageId || currentPage) as FilterPageId | undefined;

            // نام صفحه نیامده و صفحه‌ی جاری هم فیلترپذیر نیست →
            // می‌پرسیم. جواب کاربر در همان گفتگو ادامه پیدا می‌کند،
            // پس عبارتی که گفته بود دور ریخته نمی‌شود.
            if (!pageId) {
                const targets = visibleDatasets(role).filter(
                    (d) => d.filterable || d.id === "reports"
                );
                const asked = extractFilter(sentence);
                const subject = asked.search || statusLabel(asked.status) || "این عبارت";
                const text = "«" + subject + "» را در کدام صفحه نمایش بدهم؟";

                return {
                    messages: [
                        say(text, "command", {
                            actions: targets.slice(0, 8).map((d) => ({
                                label: d.title,
                                send: d.title,
                            })),
                        }),
                    ],
                    pending: {
                        datasetId: PAGE_CHOICE,
                        awaiting: PAGE_CHOICE,
                        values: {
                            sentence,
                            ...(asked.search ? { search: asked.search } : {}),
                            ...(asked.status ? { status: asked.status } : {}),
                        },
                        options: targets.map((d) => ({ value: d.id, label: d.title })),
                    },
                    spoken: text,
                };
            }

            const dataset = datasetById(pageId);
            if (!dataset || !canAccess(role, dataset.roles)) {
                const text = "دسترسی شما به این بخش باز نیست.";
                return { messages: [say(text, "command")], pending: null, spoken: text };
            }

            const extracted = extractFilter(sentence, dataset);
            const values: Record<string, string> = {};
            if (extracted.search) values.search = extracted.search;
            if (extracted.status) values.status = extracted.status;
            const probed = readSlotValue(RANGE_PROBE, sentence);
            if (probed) values.range = probed;

            const filter = buildPageFilter(dataset, values, sentence);
            if (!filter) {
                const text = "چی رو فیلتر کنم؟ اسم، تاریخ یا وضعیتش رو بگید.";
                return { messages: [say(text, "command")], pending: null, spoken: text };
            }

            const shown = describeFilter(filter);
            const target =
                currentPage === pageId
                    ? ({ type: "apply-filter", filter, title: dataset.title } as Effect)
                    : ({
                          type: "navigate",
                          link: dataset.link,
                          title: dataset.title,
                          filter,
                      } as Effect);

            // «فیلتر کن زهرا حیدری» یعنی هم صفحه را فیلتر کن، هم
            // بگو چه چیزی پیدا شد. فقط گفتن «فیلتر گذاشتم» برای
            // کاربری که دارد با گوش کار می‌کند، هیچ اطلاعاتی ندارد.
            if (values.search) {
                // «سهمیه» هم مثل بقیه داده را می‌خواند. قبلاً استثنا شده
                // بود تا فیلتر زودتر اعمال شود، ولی «فیلتر گذاشتم» برای
                // کاربری که با گوش کار می‌کند هیچ اطلاعاتی ندارد — و
                // حالا که جمله‌ی همه‌ی جواب‌ها از مدل می‌آید، آن چند صد
                // میلی‌ثانیه تفاوتی در تجربه نمی‌سازد.
                //
                // جمله‌ی خام لازم است: مجموعه‌داده‌هایی که پاسخ گفتگویی
                // می‌دهند، نام ماه را از خود جمله برمی‌دارند.
                const outcome = await runDataset(
                    dataset,
                    { ...values, sentence },
                    sentence,
                    role
                );
                const first = outcome.messages[0];

                if (first) {
                    const messages = outcome.messages.slice();
                    messages[0] = {
                        ...first,
                        text:
                            first.text +
                            "\nفیلتر «" + shown + "» را روی صفحه‌ی «" + dataset.title + "» هم گذاشتم.",
                    };
                    return { ...outcome, messages, effect: target };
                }
            }

            if (currentPage === pageId) {
                const text = "باشه، همین‌جا «" + shown + "» رو براتون فیلتر کردم.";
                return {
                    messages: [say(text, "command")],
                    pending: null,
                    effect: target,
                    spoken: text,
                };
            }

            const text = "الان صفحه‌ی «" + dataset.title + "» رو با فیلتر «" + shown + "» باز می‌کنم.";
            return {
                messages: [
                    say(text, "command", {
                        link: { label: "باز کردن " + dataset.title, to: dataset.link },
                    }),
                ],
                pending: null,
                effect: target,
                spoken: text,
            };
        }

        // ---------- برداشتن فیلتر ----------
        //
        // فیلتر خالی، نه «نبودِ فیلتر»: صفحه باید کادر جست‌وجویش را
        // پاک کند، پس search عمداً رشته‌ی خالی است و نه undefined.
        case "clear-filter": {
            const pageId = (intent.pageId || currentPage) as FilterPageId | undefined;

            if (!pageId) {
                const text = "روی صفحه‌ای نیستید که فیلتر داشته باشد. نام صفحه را بگویید.";
                return { messages: [say(text, "command")], pending: null, spoken: text };
            }

            const dataset = datasetById(pageId);
            if (!dataset || !canAccess(role, dataset.roles)) {
                const text = "دسترسی شما به این بخش باز نیست.";
                return { messages: [say(text, "command")], pending: null, spoken: text };
            }

            const filter: AssistantFilter = { pageId: pageId as FilterPageId, search: "" };
            const text = "فیلتر «" + dataset.title + "» رو پاک کردم؛ حالا همه‌ی رکوردها رو می‌بینید.";

            if (currentPage === pageId) {
                return {
                    messages: [say(text, "command")],
                    pending: null,
                    effect: { type: "apply-filter", filter, title: dataset.title },
                    spoken: text,
                };
            }

            return {
                messages: [say(text, "command")],
                pending: null,
                effect: { type: "navigate", link: dataset.link, title: dataset.title, filter },
                spoken: text,
            };
        }

        case "menu": {
            const text = menuText(role);
            return {
                messages: [say(text, "command", { actions: menuActions(role) })],
                pending: null,
                spoken: "نقش شما " + roleLabel(role) + " است. فهرست دسترسی‌ها را نشان دادم.",
            };
        }

        case "help": {
            const text = helpText(role);
            return {
                messages: [say(text, "command", { actions: menuActions(role) })],
                pending: null,
                spoken: "راهنما را نشان دادم.",
            };
        }

        case "whoami": {
            const user = currentUsername();
            const text =
                "نام کاربری: " + (user || "نامشخص") + "\nنقش: " + roleLabel(role) +
                "\nتعداد صفحات در دسترس: " + visiblePages(role).length;
            return { messages: [say(text, "command")], pending: null, spoken: "نقش شما " + roleLabel(role) + " است." };
        }

        case "close":
            return {
                messages: [say(intent.reply, "command")],
                pending: null,
                effect: { type: "close" },
                spoken: intent.reply,
            };

        case "clear":
            return {
                messages: [],
                pending: null,
                effect: { type: "clear" },
                spoken: "",
            };

        case "cancel":
            return {
                messages: [say(intent.reply, "command")],
                pending: null,
                spoken: intent.reply,
            };

        case "voice-on":
        case "voice-off":
        case "speak-on":
        case "speak-off":
            return {
                messages: [say(intent.reply, "command")],
                pending: null,
                effect: { type: intent.type },
                spoken: intent.reply,
            };

        default:
            return { messages: [], pending: null, spoken: "" };
    }
};

/** دستورهایی که حتی وسط یک گفتگوی نیمه‌کاره هم باید کار کنند. */
const INTERRUPTIBLE: Intent["type"][] = [
    "cancel", "clear", "close", "voice-on", "voice-off", "speak-on", "speak-off", "help",
];

// ============================================================
// پاسخ
// ============================================================

export const respond = async (input: RespondInput): Promise<Outcome> => {
    const { message, role, pending } = input;
    const trimmed = String(message || "").trim();

    if (!trimmed) {
        return { messages: [], pending, spoken: "" };
    }

    const intent = detectIntent(trimmed, { role });

    /*
      تعارف، قبل از هر چیز دیگر و بدون مدل.

      «سلام خوبی» با مسیر مدل چهار ثانیه طول می‌کشید (۱.۵ ثانیه مدل +
      ۲ ثانیه ساخت صدا). جوابش ثابت است، پس رفتن به مدل فقط انتظار و
      هزینه است.

      شرطِ «هیچ گفتگوی نیمه‌کاره‌ای در جریان نباشد» لازم است: اگر
      دستیار نام خبازی را پرسیده و کاربر «باشه» گفته، آن «باشه» جوابِ
      سوال است، نه تعارف.
    */
    if (!pending) {
        const chat = smallTalkReply(trimmed);
        if (chat) {
            return { messages: [say(chat, "knowledge")], pending: null, spoken: chat };
        }
    }

    // ---------- گفتگوی نیمه‌کاره ----------
    if (pending) {
        if (intent && INTERRUPTIBLE.indexOf(intent.type) !== -1) {
            return handleIntent(intent, role, trimmed, input.currentPage);
        }

        // ---------- پاسخ به «در کدام صفحه؟» ----------
        if (pending.datasetId === PAGE_CHOICE) {
            const answer = normalize(trimmed);
            let chosen: Dataset | undefined;

            (pending.options || []).forEach((option) => {
                if (chosen) return;
                if (
                    answer === normalize(option.label) ||
                    answer === normalize(option.value) ||
                    answer.indexOf(normalize(option.label)) !== -1
                ) {
                    chosen = datasetById(option.value);
                }
            });

            // شاید نام صفحه را جور دیگری گفته باشد؛ با تگ‌های صفحه هم می‌سنجیم
            if (!chosen) {
                const guess = matchDataset(trimmed + " نمایش بده");
                if (guess) chosen = guess.dataset;
            }

            if (!chosen) {
                const retry = "اسم صفحه رو متوجه نشدم. می‌گید کدوم صفحه رو باز کنم؟";
                return {
                    messages: [say(retry, "command", {
                        actions: (pending.options || []).map((o) => ({ label: o.label, send: o.label })),
                    })],
                    pending,
                    spoken: retry,
                };
            }

            const target = chosen as Dataset;

            if (!canAccess(role, target.roles)) {
                const text = "دسترسی شما به «" + target.title + "» باز نیست.";
                return { messages: [say(text, "command")], pending: null, spoken: text };
            }

            // عبارتی که کاربر اول گفته بود، با نام صفحه‌ی تازه ترکیب می‌شود
            const original = String(pending.values.sentence || "");
            const merged = extractFilter(original, target);
            const values: Record<string, string> = {};
            if (merged.search || pending.values.search) {
                values.search = merged.search || pending.values.search;
            }
            if (merged.status || pending.values.status) {
                values.status = (merged.status || pending.values.status) as string;
            }
            const probedRange = readSlotValue(RANGE_PROBE, original);
            if (probedRange) values.range = probedRange;

            const filter = buildPageFilter(target, values, original);
            const shown = filter ? describeFilter(filter) : "";

            if (input.currentPage === target.id && filter) {
                const text = "باشه، همین‌جا «" + shown + "» رو براتون فیلتر کردم.";
                return {
                    messages: [say(text, "command")],
                    pending: null,
                    effect: { type: "apply-filter", filter, title: target.title },
                    spoken: text,
                };
            }

            const text =
                "الان «" + target.title + "» رو" + (shown ? " با فیلتر «" + shown + "»" : "") + " باز می‌کنم.";
            return {
                messages: [
                    say(text, "command", {
                        link: { label: "باز کردن " + target.title, to: target.link },
                    }),
                ],
                pending: null,
                effect: { type: "navigate", link: target.link, title: target.title, filter },
                spoken: text,
            };
        }

        const dataset = datasetById(pending.datasetId);
        if (!dataset) {
            return {
                messages: [say("سوال قبلی منقضی شد. دوباره بپرسید.", "system")],
                pending: null,
                spoken: "سوال قبلی منقضی شد.",
            };
        }

        const slot = dataset.slots.filter((s) => s.key === pending.awaiting)[0];
        const values: Record<string, string> = { ...pending.values };

        let value: string | null = null;

        // در choice، اول برچسب گزینه‌ها را می‌سنجیم (کاربر روی دکمه زده
        // یا عین متن را گفته) و بعد تشخیص کلمه‌ای.
        if (slot && slot.kind === "choice" && pending.options) {
            const n = normalize(trimmed);
            for (let i = 0; i < pending.options.length; i++) {
                const option = pending.options[i];
                if (
                    n === normalize(option.label) ||
                    n === normalize(option.value) ||
                    n.indexOf(normalize(option.label)) !== -1
                ) {
                    value = option.value;
                    break;
                }
            }
        }

        if (value === null && slot) value = readSlotValue(slot, trimmed);

        if (value === null) {
            const retry =
                slot && slot.kind === "daterange"
                    ? "تاریخ را نفهمیدم. مثلاً بگویید «دیروز»، «۱۴۰۴/۰۳/۱۲» یا «از ۱۴۰۴/۰۳/۰۱ تا ۱۴۰۴/۰۳/۱۰»."
                    : "متوجه نشدم. " + (slot ? slot.prompt : "دوباره بگویید.");
            return {
                messages: [
                    say(retry, "data", {
                        actions: pending.options
                            ? pending.options.slice(0, 12).map((o) => ({ label: o.label, send: o.label }))
                            : undefined,
                    }),
                ],
                pending,
                spoken: retry,
            };
        }

        values[pending.awaiting] = value;

        const next = missingSlot(dataset, values);
        if (next) return askForSlot(dataset, values, next);

        // سوال اصلی کاربر، نه جوابِ تک‌کلمه‌ای او به سوال اسلات
        return runDataset(dataset, values, originalQuestion(input), role);
    }

    // ---------- «اطلاعاتش را بگو» ----------
    //
    // قبل از handleIntent، چون تنها دستوری است که به نتیجه‌ی قبلی
    // نیاز دارد و handleIntent آن را در دست ندارد. اگر آخرین کار
    // فیلتر صفحه‌ی سهمیه بوده، نتیجه هنوز در گفتگو نیست؛ همان جست‌وجو
    // را اینجا به نتیجه‌ی ماهانه تبدیل می‌کنیم تا «اطلاعاتشو بده» کار کند.
    if (intent && intent.type === "describe") {
        const previousUserTurn = input.history
            .slice()
            .reverse()
            .find((turn) => turn.role === "user");
        const previousIntent = previousUserTurn
            ? detectIntent(previousUserTurn.content, { role })
            : null;

        if (
            input.currentPage === "quota" &&
            previousUserTurn &&
            previousIntent?.type === "filter" &&
            (!previousIntent.pageId || previousIntent.pageId === "quota")
        ) {
            const dataset = datasetById("quota");
            const extracted = dataset ? extractFilter(previousUserTurn.content, dataset) : {};
            if (dataset && canAccess(role, dataset.roles) && extracted.search) {
                return runDataset(
                    dataset,
                    { search: extracted.search, sentence: previousUserTurn.content },
                    previousUserTurn.content,
                    role
                );
            }
        }

        const text = input.lastResult
            ? narrateResult(input.lastResult)
            : "هنوز نتیجه‌ای نیاورده‌ام که بخوانم. اول بپرسید یا فیلتر کنید.";

        return { messages: [say(text, "data")], pending: null, spoken: text };
    }

    // ---------- لایه‌ی اول، بخش دستور ----------
    if (intent) return handleIntent(intent, role, trimmed, input.currentPage);

    // ---------- لایه‌ی اول، بخش داده ----------
    const hit = matchDataset(trimmed);
    if (hit) {
        if (!canAccess(role, hit.dataset.roles)) {
            const text =
                "«" + hit.dataset.title + "» برای نقش شما (" + roleLabel(role) + ") باز نیست.";
            return { messages: [say(text, "data")], pending: null, spoken: text };
        }

        const values = extractSlots(hit.dataset, trimmed);
        const next = missingSlot(hit.dataset, values);
        if (next) return askForSlot(hit.dataset, values, next);

        const outcome = await runDataset(hit.dataset, values, trimmed, role);
        return withPageHandoff(outcome, hit.dataset, values, trimmed, input.currentPage);
    }

    // ---------- «فلانی رو نشون بده» روی صفحه‌ای که باز است ----------
    //
    // نام بخش نیامده، ولی کاربر روی یک صفحه‌ی فیلترپذیر است. این
    // *بعد از* تطبیق مجموعه‌داده می‌آید: اگر نام بخش در جمله بود،
    // همان برنده است.
    if (input.currentPage && hasAny(normalize(trimmed), SHOW_VERBS)) {
        const dataset = datasetById(input.currentPage);
        if (dataset && canAccess(role, dataset.roles)) {
            const extracted = extractFilter(trimmed, dataset);
            if (extracted.search || extracted.status) {
                const filter: AssistantFilter = {
                    pageId: dataset.id as FilterPageId,
                    search: extracted.search,
                    status: dataset.hasStatusTab ? extracted.status : undefined,
                };
                const shown = filter.search || statusLabel(filter.status);
                const text = "باشه، همین‌جا «" + shown + "» رو براتون فیلتر کردم.";
                return {
                    messages: [say(text, "command")],
                    pending: null,
                    effect: { type: "apply-filter", filter, title: dataset.title },
                    spoken: text,
                };
            }
        }
    }

    // ---------- لایه‌ی دوم: پایگاه دانش محلی ----------
    const offline = findOfflineAnswer(trimmed, role);

    // نسخه‌ی OpenAI فقط برای گفت‌وگوی آزاد است؛ دستورهای صفحه و
    // دسترسی داده بالا‌تر اجرا شده‌اند و رفتار نسخه‌ی فعلی هم
    // با حالت پیش‌فرض hybrid دست‌نخورده می‌ماند.
    if (isOpenAiMode()) {
        if (!isLlmEnabled()) {
            const text = "اتصال OpenAI هنوز تنظیم نشده است. تنظیمات سرور را بررسی کنید.";
            return { messages: [say(text, "fallback")], pending: null, spoken: text };
        }

        const reply = await askLlm({
            message: trimmed,
            history: input.history,
            context: {
                roleLabel: roleLabel(role),
                allowedPages: visiblePages(role).map((p) => p.title),
                allowedDatasets: visibleDatasets(role).map((d) => d.title),
                lastResultSummary: canSendResults() ? input.lastResultSummary : undefined,
            },
        });
        if (reply) return { messages: [say(reply, "llm")], pending: null, spoken: reply };

        // مدل جواب نداد (سرور خاموش، مهلت تمام، خطای شبکه). کاربر نباید
        // دست خالی بماند: اگر پایگاه دانش محلی جوابی دارد همان را
        // می‌دهیم و فقط یک خط توضیح اضافه می‌کنیم.
        if (offline) {
            const text =
                offline.answer +
                "\n\n(این جواب از دانش محلی سامانه است؛ ارتباط با هوش مصنوعی برقرار نشد.)";
            return { messages: [say(text, "knowledge")], pending: null, spoken: offline.answer };
        }

        // پایگاه دانش هم جوابی نداشت: به جای یک پیام خطای خشک، همان
        // منوی نقش کاربر را پیشنهاد می‌دهیم — دست خالی ماندن بدترین
        // حالت است.
        const text =
            "ارتباط با هوش مصنوعی برقرار نشد، ولی این کارها همیشه محلی انجام می‌شوند:\n\n" +
            menuText(role);
        return {
            messages: [say(text, "fallback", { actions: menuActions(role) })],
            pending: null,
            spoken: "ارتباط با هوش مصنوعی برقرار نشد.",
        };
    }

    if (offline && offline.score >= 5) {
        return {
            messages: [say(offline.answer, "knowledge")],
            pending: null,
            spoken: offline.answer,
        };
    }

    // ---------- لایه‌ی سوم: مدل زبانی (اگر پیکربندی شده باشد) ----------
    if (isLlmEnabled()) {
        const reply = await askLlm({
            message: trimmed,
            history: input.history,
            context: {
                roleLabel: roleLabel(role),
                allowedPages: visiblePages(role).map((p) => p.title),
                allowedDatasets: visibleDatasets(role).map((d) => d.title),
                lastResultSummary: canSendResults() ? input.lastResultSummary : undefined,
            },
        });
        if (reply) {
            return { messages: [say(reply, "llm")], pending: null, spoken: reply };
        }
    }

    // ---------- پاسخ محلی ضعیف‌تر، بهتر از هیچ ----------
    if (offline) {
        return {
            messages: [say(offline.answer, "knowledge")],
            pending: null,
            spoken: offline.answer,
        };
    }

    // ---------- بازگشت نهایی ----------
    const fallback =
        "ببخشید، درست متوجه نشدم. می‌تونم اطلاعات رو پیدا کنم یا صفحه‌ی موردنظرتون رو باز کنم.";
    return {
        messages: [
            say(fallback + "\n\n" + menuText(role), "fallback", { actions: menuActions(role) }),
        ],
        pending: null,
        spoken: fallback,
    };
};
